import { useState } from "react";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { CrossPromo } from "../components/CrossPromo.jsx";
import { HomeButton } from "../components/HomeButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const BASE_POINTS = {
  "A+": 4.0, "A": 4.0, "A-": 3.7, "B+": 3.3, "B": 3.0, "B-": 2.7,
  "C+": 2.3, "C": 2.0, "C-": 1.7, "D+": 1.3, "D": 1.0, "D-": 0.7, "F": 0.0,
};
const WEIGHT_BOOSTS = { "Regular": 0, "Honors": 0.5, "AP": 1.0, "IB": 1.0, "Dual Enrollment": 1.0 };
const GRADE_OPTIONS = Object.keys(BASE_POINTS);
const WEIGHT_OPTIONS = Object.keys(WEIGHT_BOOSTS);
const CREDIT_OPTIONS = ["1","2","3","4","5","6"];

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

const inputStyle = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1px solid rgba(20,184,166,0.12)", background: "rgba(0,0,0,0.3)",
  color: "#E2E8F0", fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif",
  outline: "none", boxSizing: "border-box",
};

function NumInput({ value, onChange, placeholder, label, suffix }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>}
      <div style={{ position: "relative" }}>
        <input type="number" value={value} onChange={e => onChange(e.target.value)}
          placeholder={placeholder} style={{ ...inputStyle, paddingRight: suffix ? 36 : 14 }} />
        {suffix && <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#4B5563" }}>{suffix}</span>}
      </div>
    </div>
  );
}

function Sel({ value, onChange, options, label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      {label && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, cursor: "pointer", fontSize: 14 }}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function Result({ label, value, sub, color = "#5EEAD4" }) {
  return (
    <div style={{
      padding: "20px 16px", borderRadius: 14, textAlign: "center", flex: 1, minWidth: 120,
      background: `linear-gradient(135deg, ${rgba(color,0.06)}, ${rgba(color,0.02)})`,
      border: `1px solid ${rgba(color,0.15)}`,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color, letterSpacing: "-0.5px" }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#4B5563", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function Btn({ children, onClick, secondary, style: s = {} }) {
  const [h, setH] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        padding: "12px 28px", borderRadius: 12, cursor: "pointer",
        fontSize: 14, fontWeight: 600, fontFamily: "'DM Sans', system-ui, sans-serif",
        background: secondary ? "rgba(20,184,166,0.08)" : "linear-gradient(135deg, #14B8A6, #0EA5E9)",
        color: secondary ? "#5EEAD4" : "#fff",
        border: secondary ? "1px solid rgba(20,184,166,0.15)" : "none",
        boxShadow: h && !secondary ? "0 0 24px rgba(20,184,166,0.3)" : "none",
        transform: h ? "translateY(-1px)" : "none", transition: "all 0.2s ease", ...s,
      }}>{children}</button>
  );
}

function FinalGradeCalc() {
  const [cur, setCur] = useState("");
  const [want, setWant] = useState("");
  const [weight, setWeight] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    const c = parseFloat(cur), w = parseFloat(want), fw = parseFloat(weight) / 100;
    if (isNaN(c) || isNaN(w) || isNaN(fw) || fw <= 0 || fw > 1) { setRes({ err: true }); return; }
    const needed = Math.round(((w - c * (1 - fw)) / fw) * 100) / 100;
    setRes({ needed, ok: needed <= 100 });
  };

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 24, lineHeight: 1.6 }}>
        Enter your current grade, the grade you want, and how much the final is worth.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>
        <NumInput label="Current Grade" value={cur} onChange={setCur} placeholder="85" suffix="%" />
        <NumInput label="Desired Grade" value={want} onChange={setWant} placeholder="90" suffix="%" />
        <NumInput label="Final Weight" value={weight} onChange={setWeight} placeholder="30" suffix="%" />
      </div>
      <Btn onClick={calc} style={{ width: "100%" }}>Calculate What I Need</Btn>
      {res && !res.err && (
        <div style={{ marginTop: 24 }}>
          <Result label="You need on the final" value={`${res.needed}%`}
            color={res.needed > 100 ? "#F87171" : res.needed > 90 ? "#FBBF24" : "#5EEAD4"}
            sub={res.needed > 100 ? "Not mathematically possible" : res.needed <= 0 ? "You already have it!" : res.needed <= 70 ? "Very achievable" : res.needed <= 90 ? "Doable with solid studying" : "Ambitious — study hard!"} />
        </div>
      )}
      {res && res.err && (
        <div style={{ marginTop: 20, padding: "14px 18px", borderRadius: 12, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.15)", fontSize: 13, color: "#FCA5A5" }}>
          Please enter valid numbers (grades 0-100, weight 1-100%).
        </div>
      )}
    </div>
  );
}

function GPACalc() {
  const [courses, setCourses] = useState([
    { name: "Course 1", grade: "A", wt: "Regular", cr: "3" },
    { name: "Course 2", grade: "B+", wt: "Regular", cr: "3" },
  ]);
  const [showW, setShowW] = useState(true);
  const [showRef, setShowRef] = useState(false);

  const add = () => setCourses([...courses, { name: "Course " + (courses.length+1), grade: "A", wt: "Regular", cr: "3" }]);
  const remove = (i) => { if (courses.length > 1) setCourses(courses.filter((_, idx) => idx !== i)); };
  const update = (i, f, v) => { const u = [...courses]; u[i] = { ...u[i], [f]: v }; setCourses(u); };

  let totCr = 0, uwPts = 0, wPts = 0;
  courses.forEach(c => {
    const cr = parseFloat(c.cr) || 0;
    const bp = BASE_POINTS[c.grade] || 0;
    const boost = WEIGHT_BOOSTS[c.wt] || 0;
    totCr += cr; uwPts += bp * cr; wPts += (bp + boost) * cr;
  });
  const uwGPA = totCr > 0 ? formatNum(uwPts / totCr, 2) : "0.00";
  const wGPA = totCr > 0 ? formatNum(wPts / totCr, 2) : "0.00";
  const hasW = courses.some(c => c.wt !== "Regular");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <p style={{ fontSize: 15, color: "#6B7280", margin: 0 }}>Add your courses, grades, and course type.</p>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6B7280", cursor: "pointer", whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showW} onChange={e => setShowW(e.target.checked)} style={{ accentColor: "#14B8A6" }} />
          Weighted
        </label>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
        {courses.map((c, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "end" }}>
            <div style={{ flex: 2, display: "flex", flexDirection: "column", gap: 5 }}>
              {i === 0 && <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Course</label>}
              <input value={c.name} onChange={e => update(i, "name", e.target.value)} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}><Sel label={i === 0 ? "Grade" : null} value={c.grade} onChange={v => update(i, "grade", v)} options={GRADE_OPTIONS} /></div>
            {showW && <div style={{ flex: 1.2 }}><Sel label={i === 0 ? "Type" : null} value={c.wt} onChange={v => update(i, "wt", v)} options={WEIGHT_OPTIONS} /></div>}
            <div style={{ flex: 0.7 }}><Sel label={i === 0 ? "Credits" : null} value={c.cr} onChange={v => update(i, "cr", v)} options={CREDIT_OPTIONS} /></div>
            <button onClick={() => remove(i)} style={{
              width: 36, height: 40, borderRadius: 10, border: "1px solid rgba(248,113,113,0.15)",
              background: "rgba(248,113,113,0.05)", color: "#F87171",
              cursor: courses.length <= 1 ? "not-allowed" : "pointer",
              fontSize: 16, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center",
              opacity: courses.length <= 1 ? 0.3 : 1, marginTop: i === 0 ? 19 : 0, flexShrink: 0,
            }}>x</button>
          </div>
        ))}
      </div>

      <Btn secondary onClick={add} style={{ width: "100%", marginBottom: 24 }}>+ Add Course</Btn>

      <div style={{ display: "flex", gap: 12 }}>
        <Result label="Unweighted GPA" value={uwGPA} color="#5EEAD4" sub={totCr + " total credits"} />
        {showW && hasW && <Result label="Weighted GPA" value={wGPA} color="#7DD3FC" sub="AP/IB/DE +1.0, Honors +0.5" />}
      </div>

      <div style={{ marginTop: 16 }}>
        <div onClick={() => setShowRef(!showRef)} style={{
          padding: "12px 16px", borderRadius: 12, cursor: "pointer",
          background: "rgba(0,0,0,0.15)", border: "1px solid rgba(20,184,166,0.06)",
          fontSize: 12, color: "#4B5563",
        }}>
          {showRef ? "Hide" : "Show"} GPA scale reference
        </div>
        {showRef && (
          <div style={{ padding: "12px 16px", background: "rgba(0,0,0,0.1)", borderRadius: "0 0 12px 12px", border: "1px solid rgba(20,184,166,0.06)", borderTop: "none" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "4px 16px", fontSize: 12, color: "#6B7280" }}>
              {GRADE_OPTIONS.map(g => (
                <div key={g} style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>{g}</span><span style={{ color: "#5EEAD4" }}>{BASE_POINTS[g].toFixed(1)}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#6B7280", borderTop: "1px solid rgba(255,255,255,0.03)", paddingTop: 10, marginTop: 8 }}>
              <strong style={{ color: "#7DD3FC" }}>Weighted boosts:</strong> AP, IB, Dual Enrollment = +1.0 | Honors = +0.5
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SemesterPlanner() {
  const [cGPA, setCGPA] = useState("");
  const [cCr, setCCr] = useState("");
  const [tGPA, setTGPA] = useState("");
  const [pCr, setPCr] = useState("");
  const [res, setRes] = useState(null);

  const calc = () => {
    const a = parseFloat(cGPA), b = parseFloat(cCr), c = parseFloat(tGPA), d = parseFloat(pCr);
    if ([a,b,c,d].some(isNaN) || b < 0 || d <= 0) { setRes({ err: true }); return; }
    const needed = (c * (b + d) - a * b) / d;
    const scenarios = [
      { label: "All A's", gpa: 4.0 }, { label: "A's and B's", gpa: 3.5 },
      { label: "All B's", gpa: 3.0 }, { label: "B's and C's", gpa: 2.5 },
    ].map(s => ({ ...s, cum: ((a * b + s.gpa * d) / (b + d)).toFixed(2) }));
    setRes({ needed: Math.round(needed * 100) / 100, scenarios });
  };

  return (
    <div>
      <p style={{ fontSize: 15, color: "#6B7280", marginTop: 0, marginBottom: 24, lineHeight: 1.6 }}>
        See what GPA you need this semester to hit your target cumulative GPA.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <NumInput label="Current Cumulative GPA" value={cGPA} onChange={setCGPA} placeholder="3.2" />
        <NumInput label="Credits Completed" value={cCr} onChange={setCCr} placeholder="60" />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 24 }}>
        <NumInput label="Target Cumulative GPA" value={tGPA} onChange={setTGPA} placeholder="3.5" />
        <NumInput label="Credits This Semester" value={pCr} onChange={setPCr} placeholder="15" />
      </div>
      <Btn onClick={calc} style={{ width: "100%" }}>Calculate Semester Target</Btn>

      {res && !res.err && (
        <div style={{ marginTop: 24 }}>
          <Result label="Semester GPA Needed"
            value={res.needed > 4.0 ? ">4.0" : res.needed < 0 ? "0.00" : formatNum(res.needed, 2)}
            color={res.needed > 4.0 ? "#F87171" : res.needed > 3.5 ? "#FBBF24" : "#5EEAD4"}
            sub={res.needed > 4.0 ? "Not possible with standard grading" : res.needed <= 0 ? "Already exceeded your target!" : "Across " + pCr + " credits"} />

          <div style={{ marginTop: 16, borderRadius: 12, overflow: "hidden", border: "1px solid rgba(20,184,166,0.08)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "10px 16px", background: "rgba(20,184,166,0.04)", fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              <span>If you get</span><span style={{ textAlign: "center" }}>Sem. GPA</span><span style={{ textAlign: "right" }}>Cumulative</span>
            </div>
            {res.scenarios.map((s, i) => {
              const hits = parseFloat(s.cum) >= parseFloat(tGPA);
              return (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "10px 16px", fontSize: 13, color: "#A1A1AA", borderTop: "1px solid rgba(255,255,255,0.03)", background: hits ? "rgba(20,184,166,0.03)" : "transparent" }}>
                  <span>{s.label}</span>
                  <span style={{ textAlign: "center", color: "#5EEAD4" }}>{s.gpa.toFixed(1)}</span>
                  <span style={{ textAlign: "right", fontWeight: 600, color: hits ? "#5EEAD4" : "#6B7280" }}>
                    {s.cum} {hits ? " ✓" : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {res && res.err && (
        <div style={{ marginTop: 20, padding: "14px 18px", borderRadius: 12, background: "rgba(248,113,113,0.06)", border: "1px solid rgba(248,113,113,0.15)", fontSize: 13, color: "#FCA5A5" }}>
          Please enter valid numbers for all fields.
        </div>
      )}
    </div>
  );
}

export default function GradeCalcApp() {
  const [tab, setTab] = useState("final");
  const tabs = [
    { id: "final", label: "Final Grade", icon: "🎯", desc: "What do I need?" },
    { id: "gpa", label: "GPA Calculator", icon: "📊", desc: "Weighted & unweighted" },
    { id: "plan", label: "Semester Plan", icon: "📋", desc: "Target your GPA" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", right: "-10%", width: "50vw", height: "50vw", background: "radial-gradient(circle, rgba(20,184,166,0.05) 0%, transparent 65%)", pointerEvents: "none" }} />

      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: "1px solid rgba(20,184,166,0.06)", position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HomeButton accentColor="#14B8A6" />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "linear-gradient(135deg, #14B8A6, #0EA5E9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#fff" }}>G</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: "linear-gradient(135deg, #5EEAD4, #7DD3FC)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>GradeCalc</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 700, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <h1 style={{ fontSize: 38, fontWeight: 800, margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-1px", background: "linear-gradient(135deg, #CCFBF1, #5EEAD4 40%, #7DD3FC 70%, #BAE6FD)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Grade and GPA Calculator
          </h1>
          <p style={{ fontSize: 15, color: "#6B7280", margin: 0 }}>Supports AP, Honors, IB and Dual Enrollment weighted GPAs.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 28 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "14px 10px", borderRadius: 14, cursor: "pointer", fontFamily: "inherit",
              background: tab === t.id ? "rgba(20,184,166,0.1)" : "rgba(255,255,255,0.02)",
              border: tab === t.id ? "1.5px solid rgba(20,184,166,0.3)" : "1.5px solid rgba(255,255,255,0.05)",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
              transition: "all 0.2s ease",
            }}>
              <span style={{ fontSize: 20 }}>{t.icon}</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: tab === t.id ? "#5EEAD4" : "#6B7280" }}>{t.label}</span>
              <span style={{ fontSize: 10, color: tab === t.id ? "#14B8A6" : "#374151" }}>{t.desc}</span>
            </button>
          ))}
        </div>

        <div style={{ padding: "24px 22px", borderRadius: 20, background: "rgba(10,11,20,0.6)", border: "1px solid rgba(20,184,166,0.08)", boxShadow: "0 4px 30px rgba(0,0,0,0.2)" }}>
          {tab === "final" && <FinalGradeCalc />}
          {tab === "gpa" && <GPACalc />}
          {tab === "plan" && <SemesterPlanner />}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginTop: 40 }}>
          {[
            { icon: "🎯", title: "Final Grade", text: "See exactly what score you need on the final to get your desired grade." },
            { icon: "📊", title: "Weighted GPA", text: "AP, Honors, IB, and Dual Enrollment courses all calculated correctly." },
            { icon: "📋", title: "Semester Plan", text: "Plan what GPA you need this semester to hit your cumulative target." },
          ].map((item, i) => (
            <div key={i} style={{ padding: "18px 14px", borderRadius: 14, background: "rgba(20,184,166,0.02)", border: "1px solid rgba(20,184,166,0.05)" }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#5EEAD4", marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>{item.text}</div>
            </div>
          ))}
        </div>

        {/* Cross-promo (rotates per page load) */}
        <CrossPromo currentSlug="gradecalc" accent="#14B8A6" accent2="#7DD3FC" accentLight="#5EEAD4" />
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: "1px solid rgba(20,184,166,0.04)" }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
        input[type="number"] { -moz-appearance: textfield; }
        input::placeholder { color: #374151; }
        select { color-scheme: dark; }
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
